import type { LineString } from "geojson";
import { distanceMeters } from "./engine";
import type { Coordinates } from "./types";

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");
const MAX_ROUTE_POINTS = 20_000;
const MAX_ROUTE_STEPS = 300;
const MAX_ROUTE_NAME_LENGTH = 160;

const routeManeuverTypes = [
  "turn",
  "new name",
  "depart",
  "arrive",
  "merge",
  "on ramp",
  "off ramp",
  "fork",
  "end of road",
  "continue",
  "roundabout",
  "rotary",
  "roundabout turn",
  "notification",
  "exit roundabout",
  "exit rotary",
] as const;
export type RouteManeuverType =
  | (typeof routeManeuverTypes)[number]
  | "unknown";

const routeManeuverModifiers = [
  "uturn",
  "sharp right",
  "right",
  "slight right",
  "straight",
  "slight left",
  "left",
  "sharp left",
] as const;
export type RouteManeuverModifier =
  (typeof routeManeuverModifiers)[number];

export interface RouteDestination {
  id: string;
  name: string;
  coordinates: Coordinates;
}

export interface WalkingRoute {
  kind: "walking";
  geometry: LineString;
  distanceMeters: number;
  durationSeconds: number;
  providerName: string;
  attributionUrl: string;
  steps: WalkingRouteStep[];
}

export interface WalkingRouteStep {
  maneuver: {
    type: RouteManeuverType;
    modifier?: RouteManeuverModifier;
    exit?: number;
  };
  roadName?: string;
  distanceMeters: number;
  durationSeconds: number;
}

export interface DirectRoute {
  kind: "direct";
  geometry: LineString;
  distanceMeters: number;
  durationSeconds: number;
  reason: "offline" | "routing_unavailable";
}

export type DisplayRoute = WalkingRoute | DirectRoute;

interface RouteApiPayload {
  route?: {
    geometry?: unknown;
    distanceMeters?: unknown;
    durationSeconds?: unknown;
    steps?: unknown;
  };
  provider?: {
    name?: unknown;
    attributionUrl?: unknown;
  };
}

type FetchImplementation = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

function isCoordinate(value: unknown): value is [number, number] {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    value.every((coordinate) => typeof coordinate === "number" && Number.isFinite(coordinate)) &&
    value[0] >= -180 &&
    value[0] <= 180 &&
    value[1] >= -90 &&
    value[1] <= 90
  );
}

function parseLineString(value: unknown): LineString {
  if (!value || typeof value !== "object") throw new Error("invalid_route_geometry");
  const candidate = value as Partial<LineString>;
  if (
    candidate.type !== "LineString" ||
    !Array.isArray(candidate.coordinates) ||
    candidate.coordinates.length < 2 ||
    candidate.coordinates.length > MAX_ROUTE_POINTS ||
    !candidate.coordinates.every(isCoordinate)
  ) {
    throw new Error("invalid_route_geometry");
  }
  return {
    type: "LineString",
    coordinates: candidate.coordinates,
  };
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeManeuverType(value: unknown): RouteManeuverType {
  return typeof value === "string" &&
    (routeManeuverTypes as readonly string[]).includes(value)
    ? (value as RouteManeuverType)
    : "unknown";
}

function normalizeManeuverModifier(
  value: unknown,
): RouteManeuverModifier | undefined {
  return typeof value === "string" &&
    (routeManeuverModifiers as readonly string[]).includes(value)
    ? (value as RouteManeuverModifier)
    : undefined;
}

function parseRouteSteps(value: unknown): WalkingRouteStep[] {
  if (!Array.isArray(value)) return [];
  const steps: WalkingRouteStep[] = [];
  for (const valueStep of value.slice(0, MAX_ROUTE_STEPS)) {
    if (!isPlainRecord(valueStep) || !isPlainRecord(valueStep.maneuver)) continue;
    const distance = valueStep.distanceMeters;
    const duration = valueStep.durationSeconds;
    if (
      typeof distance !== "number" ||
      !Number.isFinite(distance) ||
      distance < 0 ||
      distance > 100_000 ||
      typeof duration !== "number" ||
      !Number.isFinite(duration) ||
      duration < 0 ||
      duration > 172_800
    ) {
      continue;
    }
    const rawName =
      typeof valueStep.roadName === "string"
        ? valueStep.roadName
            .replace(/[\u0000-\u001f\u007f]/g, " ")
            .replace(/\s+/g, " ")
            .trim()
        : "";
    const rawExit = valueStep.maneuver.exit;
    const exit =
      typeof rawExit === "number" &&
      Number.isInteger(rawExit) &&
      rawExit >= 1 &&
      rawExit <= 50
        ? rawExit
        : undefined;
    const modifier = normalizeManeuverModifier(valueStep.maneuver.modifier);
    steps.push({
      maneuver: {
        type: normalizeManeuverType(valueStep.maneuver.type),
        ...(modifier ? { modifier } : {}),
        ...(exit === undefined ? {} : { exit }),
      },
      ...(rawName
        ? { roadName: rawName.slice(0, MAX_ROUTE_NAME_LENGTH) }
        : {}),
      distanceMeters: Math.round(distance),
      durationSeconds: Math.round(duration),
    });
  }
  return steps;
}

const directionInstruction = (modifier?: RouteManeuverModifier) => {
  switch (modifier) {
    case "uturn":
      return "折り返す";
    case "sharp right":
      return "大きく右へ進む";
    case "right":
      return "右へ進む";
    case "slight right":
      return "斜め右へ進む";
    case "straight":
      return "直進する";
    case "slight left":
      return "斜め左へ進む";
    case "left":
      return "左へ進む";
    case "sharp left":
      return "大きく左へ進む";
    default:
      return "道なりに進む";
  }
};

const turnInstruction = (modifier?: RouteManeuverModifier) => {
  switch (modifier) {
    case "uturn":
      return "折り返す";
    case "sharp right":
      return "大きく右折する";
    case "right":
      return "右折する";
    case "slight right":
      return "斜め右方向へ進む";
    case "straight":
      return "直進する";
    case "slight left":
      return "斜め左方向へ進む";
    case "left":
      return "左折する";
    case "sharp left":
      return "大きく左折する";
    default:
      return "道なりに進む";
  }
};

export function formatRouteStepInstruction(step: WalkingRouteStep): string {
  const namedRoad = step.roadName ? `「${step.roadName}」` : undefined;
  const road = namedRoad ? `${namedRoad}を進む` : "道なりに進む";
  const direction = directionInstruction(step.maneuver.modifier);
  const turn = turnInstruction(step.maneuver.modifier);
  switch (step.maneuver.type) {
    case "depart":
      return namedRoad ? `${namedRoad}に沿って出発` : "出発";
    case "arrive":
      if (step.maneuver.modifier === "left") return "左側に目的地があります";
      if (step.maneuver.modifier === "right") return "右側に目的地があります";
      return "目的地に到着";
    case "turn":
      return namedRoad ? `${turn}。続いて${road}` : turn;
    case "end of road":
      return step.roadName
        ? `突き当たりで${turn}。続いて${road}`
        : `突き当たりで${turn}`;
    case "continue":
      return step.roadName ? `${direction}。続いて${road}` : direction;
    case "new name":
      return road;
    case "merge":
      return namedRoad ? `${direction}。${namedRoad}へ合流` : `${direction}。道へ合流`;
    case "on ramp":
    case "off ramp":
      return step.roadName ? `${direction}。続いて${road}` : `${direction}。接続する道へ進む`;
    case "fork":
      return step.roadName ? `分岐を${direction}。続いて${road}` : `分岐を${direction}`;
    case "roundabout":
    case "rotary":
    case "roundabout turn": {
      const exit = step.maneuver.exit
        ? `${step.maneuver.exit}番目の出口`
        : "案内された出口";
      return step.roadName
        ? `ロータリーに入り、${exit}から${road}`
        : `ロータリーに入り、${exit}へ進む`;
    }
    case "exit roundabout":
    case "exit rotary":
      return step.roadName ? `ロータリーを出て${road}` : "ロータリーを出る";
    case "notification":
      return step.roadName ? `現地案内に注意して${road}` : "現地案内に注意して進む";
    default:
      return road;
  }
}

function safeAttributionUrl(value: unknown): string {
  const fallback = "https://www.openstreetmap.org/copyright";
  if (typeof value !== "string") return fallback;
  try {
    const parsed = new URL(value);
    return parsed.protocol === "https:"
      ? parsed.toString()
      : fallback;
  } catch {
    return fallback;
  }
}

function normalizeCoordinate(coordinate: Coordinates): Coordinates {
  if (
    !Number.isFinite(coordinate.latitude) ||
    !Number.isFinite(coordinate.longitude) ||
    coordinate.latitude < -90 ||
    coordinate.latitude > 90 ||
    coordinate.longitude < -180 ||
    coordinate.longitude > 180
  ) {
    throw new Error("invalid_route_coordinate");
  }
  return {
    latitude: Number(coordinate.latitude.toFixed(6)),
    longitude: Number(coordinate.longitude.toFixed(6)),
  };
}

export function buildWalkingRouteUrl(
  apiBase = API_BASE,
): string {
  return `${apiBase}/api/v1/route`;
}

export async function fetchWalkingRoute(
  origin: Coordinates,
  destination: Coordinates,
  options: {
    signal?: AbortSignal;
    fetchImplementation?: FetchImplementation;
    apiBase?: string;
  } = {},
): Promise<WalkingRoute> {
  const fetchImplementation = options.fetchImplementation ?? fetch;
  const response = await fetchImplementation(
    buildWalkingRouteUrl(options.apiBase),
    {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        from: normalizeCoordinate(origin),
        to: normalizeCoordinate(destination),
      }),
      signal: options.signal,
    },
  );
  if (!response.ok || !response.headers.get("content-type")?.includes("application/json")) {
    throw new Error(`route_${response.status}`);
  }

  const payload = (await response.json()) as RouteApiPayload;
  const distance = payload.route?.distanceMeters;
  const duration = payload.route?.durationSeconds;
  if (
    typeof distance !== "number" ||
    !Number.isFinite(distance) ||
    distance < 0 ||
    typeof duration !== "number" ||
    !Number.isFinite(duration) ||
    duration < 0
  ) {
    throw new Error("invalid_route_metrics");
  }

  return {
    kind: "walking",
    geometry: parseLineString(payload.route?.geometry),
    distanceMeters: Math.round(distance),
    durationSeconds: Math.round(duration),
    steps: parseRouteSteps(payload.route?.steps),
    providerName:
      typeof payload.provider?.name === "string"
        ? payload.provider.name
        : "OpenStreetMap walking routing",
    attributionUrl: safeAttributionUrl(payload.provider?.attributionUrl),
  };
}

export function createDirectRoute(
  origin: Coordinates,
  destination: Coordinates,
  reason: DirectRoute["reason"],
): DirectRoute {
  const distance = distanceMeters(origin, destination);
  return {
    kind: "direct",
    geometry: {
      type: "LineString",
      coordinates: [
        [origin.longitude, origin.latitude],
        [destination.longitude, destination.latitude],
      ],
    },
    distanceMeters: distance,
    durationSeconds: Math.max(60, Math.ceil(distance / 72) * 60),
    reason,
  };
}
